import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

/**
 * PulseDot — small live-status indicator with a soft outward pulse.
 *
 * Uses CSS keyframes (no JS). Pairs nicely with a "Live" / "Online" Badge.
 */
const pulseDotVariants = cva("relative inline-flex flex-shrink-0", {
  variants: {
    size: {
      sm: "h-1.5 w-1.5",
      default: "h-2 w-2",
      lg: "h-2.5 w-2.5",
    },
    tone: {
      live: "text-emerald-500",
      success: "text-success",
      accent: "text-accent",
      destructive: "text-destructive",
      muted: "text-muted-foreground",
    },
  },
  defaultVariants: { size: "default", tone: "live" },
});

export interface PulseDotProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children">,
    VariantProps<typeof pulseDotVariants> {
  /** Disable the pulse animation (still shows the dot). */
  static?: boolean;
}

export const PulseDot = React.forwardRef<HTMLSpanElement, PulseDotProps>(
  ({ className, size, tone, static: isStatic = false, "aria-label": ariaLabel, ...props }, ref) => (
    <span
      ref={ref}
      role="status"
      aria-label={ariaLabel ?? "Live"}
      className={cn(pulseDotVariants({ size, tone }), className)}
      {...props}
    >
      {!isStatic ? (
        <span className="absolute inset-0 rounded-full bg-current opacity-60 animate-ping" aria-hidden />
      ) : null}
      <span className="relative inline-block h-full w-full rounded-full bg-current" aria-hidden />
    </span>
  ),
);
PulseDot.displayName = "PulseDot";
